/**
 * "C2 · Połączenie w modalu" — the direction the owner approved on 21 September 2026, from
 * `C2-Modal-Flow.dc.html` and `C2-Modal-Wyslano.dc.html`.
 *
 * Three faces, one dialog, and on two of them ONE ELEMENT: the form asks who, and when the write
 * lands the same popup widens from 560px to 980px under a `transition: width` while its contents
 * swap for the confirmation. That is the whole reason the faces are branches inside one
 * `Dialog.Popup` rather than two popups — a remount restarts the element and the widen never
 * happens. The third face, the seat-limit refusal, keeps its own narrower box and its own contents
 * unchanged.
 *
 * ── What the form no longer carries ──────────────────────────────────────────────────────────
 * The owner cut both on the way to C2, and neither is coming back as a smaller version of itself:
 * the intake row (the survey goes with the invitation whether or not the form says so, and the
 * confirmation states it where it is actually news) and the three-step strip (a three-field form
 * that fits on one screen is not a journey). The seat card went with them — the footer states the
 * same two numbers in the sentence the trainer was going to read anyway.
 *
 * ── What the endpoint does, and the one thing the canon draws that it does not answer ────────
 * `POST /customer/invite` opens a roster entry `Invited` and issues an offer that expires in 14
 * days; nothing is activated until the person accepts. It answers with a `GetCustomerResponse`,
 * which carries no invitation CODE — KANON 02's third card drew one, there is no field to read it
 * from, so it is not drawn rather than invented. `model/sentInvitation` carries the rest of the
 * contract, including the two paths the endpoint takes and what each of them leaves behind.
 */

import { Dialog } from '@base-ui/react/dialog';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ClientRow } from '~/entities/client';
import { useSessionUser } from '~/entities/session';

import { MailIcon, WarningIcon } from '~/shared/ui/AuthIcons';
import ember from '~/shared/ui/emberButton.module.css';

import { useInviteClient, type InviteSubmit } from '../lib/useInviteClient';
import { INVITE_FIELD_ORDER, type InviteDraft } from '../model/inviteForm';
import { InviteSentFace } from './InviteSentFace';
import { PersonIcon } from './InviteGlyphs';
import styles from './InviteClientDialog.module.css';

interface InviteClientDialogProps {
  readonly open: boolean;
  readonly triggerRef: React.RefObject<HTMLButtonElement | null>;
  readonly onClose: () => void;
  /**
   * Free the seat this occupant is holding.
   *
   * It is the roster's own act — it has the confirm, the announcement and the cache invalidation
   * already — and the roster also picks WHICH act, because an occupant is either an active client
   * (end the relationship) or an outstanding offer (take the offer back), and only one of the two
   * endpoints works on each. The panel hands the row back rather than growing a second copy of all
   * of that.
   *
   * Absent on a host that has no such act — the lead conversion on `/klienci/zgloszenia` — where the
   * panel lists who holds the seats and leaves „Zmień plan" as the way out.
   */
  readonly onFreeSeat?: ((row: ClientRow) => void) | undefined;
  /** What the form opens with — a public-form lead's name and address. Empty when absent. */
  readonly prefill?: InviteDraft | undefined;
  /** The write, when it is not `POST /customer/invite`. See `useInviteClient`. */
  readonly submit?: InviteSubmit | undefined;
  /**
   * A warning drawn over the fields, or nothing. The lead conversion uses it for an address the
   * filler never proved (public-form R13): the invitation may still go, the trainer is told first.
   */
  readonly notice?: string | null | undefined;
  /**
   * A warning on the confirmation, or nothing — what the host learned from its own write. The lead
   * conversion uses it when the answers stayed with the lead (another address was invited).
   */
  readonly sentNote?: string | null | undefined;
}

/** The warning over the fields. One dialog stands at a time, so the id need not be generated. */
const NOTICE_ID = 'invite-notice';

/** The mock draws a mark inside the first and the third box and none inside the second. */
const FIELD_GLYPH: Readonly<Partial<Record<keyof InviteDraft, typeof PersonIcon>>> = {
  firstName: PersonIcon,
  email: MailIcon,
};

const fieldLabelKeys: Readonly<Record<keyof InviteDraft, string>> = {
  firstName: 'clients.invite.firstName',
  lastName: 'clients.invite.lastName',
  email: 'clients.invite.email',
};

export function InviteClientDialog({
  open,
  triggerRef,
  onClose,
  onFreeSeat,
  prefill,
  submit,
  notice,
  sentNote,
}: InviteClientDialogProps) {
  const { t, i18n } = useTranslation();
  const user = useSessionUser();
  const hasNotice = notice !== null && notice !== undefined && notice !== '';

  // One ref per field, so a refusal can land where it has to be fixed. `firstName` doubles as the
  // dialog's opening focus.
  const firstName = useRef<HTMLInputElement>(null);
  const lastName = useRef<HTMLInputElement>(null);
  const email = useRef<HTMLInputElement>(null);
  const sentHeading = useRef<HTMLHeadingElement>(null);
  const fieldRef: Readonly<Record<keyof InviteDraft, React.RefObject<HTMLInputElement | null>>> = {
    firstName,
    lastName,
    email,
  };

  const invite = useInviteClient({
    locale: i18n.language,
    open,
    // A refused submission is otherwise silent to anyone not looking at the form: the messages
    // appear inline, and the button keeps its name and its focus (SC 3.3.1). Focus is what carries
    // the field's `aria-invalid` and its message to a listener, and it is where the fix is made.
    onRefused: (field) => {
      fieldRef[field].current?.focus();
    },
    prefill,
    submit,
  });

  // ── Focus follows the face, because a face change unmounts whatever had it ────────────────
  // Both effects answer the same defect from opposite ends: the control that was focused belongs
  // to the face that just left, so without them focus falls to `<body>` inside the popup — silent
  // to a screen reader, and one Tab away from the ✕ for everyone else.
  const done = invite.face === 'done';
  useEffect(() => {
    if (done) sentHeading.current?.focus({ preventScroll: true });
  }, [done]);

  // The OPENING mount is `initialFocus`'s, and this must not fight it — hence the latch, which is
  // set the first time the dialog shows a face that is not the form. Every later arrival at the
  // form is a return ("Zaproś kolejną osobę"), and the first field is where the trainer types.
  const onForm = invite.face === 'form';
  const hasLeftTheForm = useRef(false);
  useEffect(() => {
    if (!onForm) {
      hasLeftTheForm.current = true;
      return;
    }
    // `open` as well as the latch: `close()` resets the face, and the popup is unmounted by the
    // time this runs, so focusing here would fight the return to the trigger.
    if (open && hasLeftTheForm.current) firstName.current?.focus();
  }, [onForm, open]);

  const close = () => {
    invite.reset();
    onClose();
    // Base UI cannot find the opener on its own — it is not a `Dialog.Trigger`, because the header
    // owns the layout — and without this focus lands on `<body>`.
    triggerRef.current?.focus();
  };

  const seats = invite.seats;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        {/* Focus opens on the first FIELD, not on the ✕. The default is the first tabbable
            element in the popup, and on the form face that is the close button — so the ticket
            greeted the trainer with the way out of it. Returning `true` falls back to the default
            on the two faces that have no field. */}
        <Dialog.Popup
          className={`${styles.dialog} ${
            invite.face === 'seatLimit'
              ? styles.dialogNarrow
              : done
                ? styles.dialogWide
                : styles.dialogForm
          }`}
          initialFocus={() => firstName.current ?? true}
        >
          {invite.face === 'seatLimit' ? (
            <div className={styles.limit}>
              <div className={styles.head}>
                <div className={styles.headText}>
                  <span className={styles.limitEyebrow}>
                    {t('web:clients.inviteDialog.limit.eyebrow')}
                  </span>
                  <Dialog.Title className={styles.title}>
                    {t('web:clients.inviteDialog.limit.title', {
                      cap: invite.seats?.cap ?? invite.occupants.length,
                    })}
                  </Dialog.Title>
                </div>
                <button
                  type="button"
                  className={styles.close}
                  onClick={close}
                  aria-label={t('web:clients.inviteDialog.close')}
                >
                  <span aria-hidden>✕</span>
                </button>
              </div>

              <Dialog.Description className={styles.body}>
                {t('web:clients.inviteDialog.limit.body')}
              </Dialog.Description>

              {/* `role="list"` because `list-style: none` makes WebKit drop the list semantics
                * altogether, and then a screen reader never says how many people are holding a
                * seat — which is the whole subject of this panel. */}
              <ul className={styles.occupants} role="list">
                {invite.occupants.map((row) => {
                  const pending = row.status === 'invited';
                  return (
                    <li key={row.rowKey} className={styles.occupant}>
                      <span className={styles.occupantAvatar} aria-hidden>
                        {row.initials}
                      </span>
                      <span className={styles.occupantName}>{row.fullName}</span>
                      <span
                        className={`${styles.occupantStatus} ${
                          pending ? styles.occupantPending : ''
                        }`}
                      >
                        {pending ? t('clients.invited.pending') : t('clients.status.active')}
                      </span>
                      {/* Two different acts under one column. An outstanding offer has no
                        * relationship to end, so it says — and does — the thing that actually
                        * releases its seat. The sr-only tail names the row rather than an
                        * `aria-label`, which would REPLACE the visible label and fail SC 2.5.3
                        * on a list where every button otherwise reads the same. */}
                      {onFreeSeat === undefined ? null : (
                        <button
                          type="button"
                          className={styles.end}
                          onClick={() => {
                            close();
                            onFreeSeat(row);
                          }}
                        >
                          {pending
                            ? t('web:clients.inviteDialog.limit.withdraw')
                            : t('web:clients.inviteDialog.limit.end')}
                          <span className={styles.hidden}> — {row.fullName}</span>
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>

              <div className={styles.foot}>
                {/* The other way out of a full roster, and since 21 September 2026 a real one:
                  * the Subskrypcja tab sells the higher levels (requirement R13 names this panel
                  * as the link into it). It wears the footer's own ghost, beside „Zamknij",
                  * because ending a collaboration and buying a seat are two answers to one
                  * question and neither is the primary. */}
                <Link
                  to="/konto"
                  search={{ tab: 'subskrypcja' }}
                  className={`${styles.ghost} ${styles.ghostTrailing}`}
                  onClick={close}
                >
                  {t('web:clients.inviteDialog.limit.changePlan')}
                </Link>
                <button type="button" className={styles.ghost} onClick={close}>
                  {t('web:clients.inviteDialog.limit.close')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ONE close button for both remaining faces, positioned over the corner rather than
                * laid out in a header — which is what lets the form's own heading block and the
                * confirmation's two columns each own their whole width. */}
              <button
                type="button"
                className={`${styles.close} ${styles.closeCorner}`}
                onClick={close}
                aria-label={t('web:clients.inviteDialog.close')}
              >
                <span aria-hidden>✕</span>
              </button>

              {invite.sent !== null && done ? (
                <InviteSentFace
                  sent={invite.sent}
                  seatsAfter={invite.seatsAfter}
                  note={sentNote ?? null}
                  headingRef={sentHeading}
                  trainer={
                    user === null ? null : { firstName: user.firstName, lastName: user.lastName }
                  }
                  // „Zaproś kolejną osobę" reopens an EMPTY form on the plain invitation. A host that
                  // brought its own person and its own write (a lead's conversion) has no next
                  // person to offer: reset would reopen the same one on the same, now spent, route.
                  onAgain={
                    prefill === undefined && submit === undefined
                      ? () => {
                          invite.reset();
                        }
                      : undefined
                  }
                  onClose={close}
                />
              ) : (
                /* `noValidate`, and it is not a preference. `type="email"` hands validation to the
                   browser, which draws its own bubble in the BROWSER's language — "Please include an
                   '@' in the email address" over a Polish form — positions it where we cannot see
                   it, and refuses the submit before `inviteForm` is ever consulted, leaving
                   `web:clients.inviteDialog.emailInvalid` dead. Validation belongs to `inviteForm`,
                   which already owns the shape, the required rule and the length cap. */
                <form
                  className={styles.form}
                  noValidate
                  // The button's label changes to "Wysyłanie…", which a listener only hears if
                  // they happen to be on it. `aria-busy` says the whole form is working.
                  aria-busy={invite.submitting}
                  onSubmit={(event) => {
                    event.preventDefault();
                    invite.submit();
                  }}
                >
                  <div className={styles.formHead}>
                    <span className={styles.eyebrow}>{t('web:clients.inviteDialog.eyebrow')}</span>
                    <Dialog.Title className={styles.formHeading}>
                      {t('web:clients.inviteDialog.heading')}
                    </Dialog.Title>
                  </div>

                  {!hasNotice ? null : (
                    <p className={styles.notice} id={NOTICE_ID}>
                      <WarningIcon className={styles.noticeIcon} />
                      <span>{notice}</span>
                    </p>
                  )}

                  <div className={styles.fields}>
                    {/* ── The error is a SIBLING of the label, not a child of it ───────────────
                      * A `<label>` contributes ALL of its text to the accessible name of the control
                      * inside it, so wrapping the message made the field announce itself as
                      * "IMIĘ Uzupełnij to pole" — the refusal read as part of the field's name and
                      * the trainer heard it again from `aria-describedby`. `htmlFor` associates the
                      * label with the input explicitly, which leaves the message outside both. */}
                    {INVITE_FIELD_ORDER.map((field) => {
                      const errorKey = invite.errors[field];
                      const inputId = `invite-${field}`;
                      const errorId = `invite-${field}-error`;
                      const Glyph = FIELD_GLYPH[field];
                      return (
                        <div
                          key={field}
                          className={`${styles.field} ${
                            field === 'email' ? styles.fieldWide : ''
                          }`}
                        >
                          <label
                            className={`${styles.label} ${errorKey !== null ? styles.labelInvalid : ''}`}
                            htmlFor={inputId}
                          >
                            {t(fieldLabelKeys[field])}
                          </label>
                          <span
                            className={`${styles.box} ${errorKey !== null ? styles.boxInvalid : ''}`}
                          >
                            {Glyph !== undefined && <Glyph className={styles.boxGlyph} />}
                            <input
                              id={inputId}
                              ref={fieldRef[field]}
                              className={styles.input}
                              type={field === 'email' ? 'email' : 'text'}
                              autoComplete={
                                field === 'email'
                                  ? 'email'
                                  : field === 'firstName'
                                    ? 'given-name'
                                    : 'family-name'
                              }
                              value={invite.draft[field]}
                              // All three are required and `inviteForm` refuses every one of them, so
                              // the trainer is told before they fill the form rather than after.
                              // `aria-required` rather than `required`: the constraint is ours, and the
                              // form is `noValidate` precisely so the browser does not answer for it.
                              aria-required
                              aria-invalid={errorKey !== null}
                              // The notice is about the ADDRESS (R13: never proven), so the address
                              // field carries it, beside its own refusal when there is one.
                              aria-describedby={
                                [
                                  errorKey !== null ? errorId : null,
                                  field === 'email' && hasNotice ? NOTICE_ID : null,
                                ]
                                  .filter((id) => id !== null)
                                  .join(' ') || undefined
                              }
                              onChange={(event) => {
                                invite.setField(field, event.target.value);
                              }}
                            />
                          </span>
                          {errorKey !== null && (
                            <span className={styles.error} id={errorId}>
                              {t(errorKey)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {invite.formErrorKey !== null && (
                      <span className={`${styles.error} ${styles.fieldWide}`} role="alert">
                        {t(invite.formErrorKey)}
                      </span>
                    )}
                  </div>

                  <div className={styles.foot}>
                    {/* Nothing at all on an unlimited plan: "have I got room" is not a question
                      * that plan asks, and a count with no cap in it restates the roster header. */}
                    <span className={styles.hint}>
                      {seats !== null && !seats.unlimited && (
                        <Trans
                          i18nKey="web:clients.inviteDialog.seatLine"
                          values={{ taken: seats.taken, cap: seats.cap }}
                          components={{ b: <b className={styles.strong} /> }}
                        />
                      )}
                    </span>
                    <button type="button" className={styles.ghost} onClick={close}>
                      {t('web:clients.inviteDialog.cancel')}
                    </button>
                    {/* `aria-disabled` rather than `disabled`, so the control keeps its place in the
                      * tab order — but the attribute alone stops NOTHING, and a second press posted a
                      * second client. The click is cancelled outright while the write is running, so
                      * the form's `onSubmit` is never reached; `submit()` guards the Enter path. */}
                    <button
                      type="submit"
                      className={`${ember.ember} ${styles.cta}`}
                      aria-disabled={invite.submitting}
                      onClick={
                        invite.submitting
                          ? (event) => {
                              event.preventDefault();
                            }
                          : undefined
                      }
                    >
                      {invite.submitting
                        ? t('web:clients.inviteDialog.submitting')
                        : t('web:clients.inviteDialog.submit')}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
